import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import ar from './locales/ar.json';
import en from './locales/en.json';

export const SUPPORTED_LANGUAGES = ['ar', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const LANGUAGE_DIRECTION: Record<SupportedLanguage, 'rtl' | 'ltr'> = {
  ar: 'rtl',
  en: 'ltr',
};

export const DEFAULT_LANGUAGE: SupportedLanguage = 'ar';

export function isSupportedLanguage(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/** Keeps `<html lang>` and `<html dir>` in step with the active language. */
export function applyDocumentLanguage(language: string): void {
  const lng = isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
  document.documentElement.lang = lng;
  document.documentElement.dir = LANGUAGE_DIRECTION[lng];
}

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      ar: { translation: ar },
      en: { translation: en },
    },
    // Arabic-first per open question #6. If the owner answers "English-first",
    // this constant and the <html> attributes in index.html are the only changes.
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED_LANGUAGES,
    defaultNS: 'translation',
    detection: {
      // localStorage only — deliberately NOT 'navigator'. Open question #6 specifies
      // Arabic-first, and most staff phones report en-US, so including navigator
      // silently served English to every first-time visitor. A remembered choice
      // wins; everyone else gets Arabic via fallbackLng.
      order: ['localStorage'],
      lookupLocalStorage: 'crm.language',
      caches: ['localStorage'],
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

i18n.on('languageChanged', applyDocumentLanguage);
applyDocumentLanguage(i18n.language);

export default i18n;
